import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { useApi } from "@/hooks/useApi";
import { useActiveGithubOrgForUser } from "@/hooks/useConnectedGithubOrgs";

// Extended Check interface for run-level tests
export interface RunCheck {
  id: string;
  test_name: string;
  test_suite: string | null;
  test_status: "pass" | "fail" | "skip" | "error";
  test_type?: "junit" | "auto";
  duration_seconds?: number;
  logs?: string;
  step_record_id?: string;
  step_order?: number;
  log_line_start?: number;
  log_line_end?: number;
  branch?: string | null;
  // Additional fields for run-level tests
  job_id: string;
  job_name?: string;
}
export interface RunTestsResponse {
  tests: RunCheck[];
}
async function fetchRunTests(api: ReturnType<typeof useApi>, org: string, runId: string): Promise<RunCheck[]> {
  const url = `user/github/orgs/${org}/metrics/actions/workflows/runs/${runId}/tests`;
  const response = await api.get<RunTestsResponse>(url);
  return response.data.tests;
}
export function useRunTests(runId, t0) {
  const $ = _c(21);
  const enabled = t0 === undefined ? true : t0;
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t1;
  if ($[0] !== activeGithubOrg || $[1] !== runId) {
    t1 = ["runTests", activeGithubOrg, runId];
    $[0] = activeGithubOrg;
    $[1] = runId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const testsQueryKey = t1;
  let t2;
  if ($[3] !== activeGithubOrg || $[4] !== api || $[5] !== runId) {
    t2 = async () => {
      if (!activeGithubOrg || !runId) {
        return [];
      }
      return await fetchRunTests(api, activeGithubOrg, runId);
    };
    $[3] = activeGithubOrg;
    $[4] = api;
    $[5] = runId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = enabled && !!activeGithubOrg && !!runId;
  let t4;
  if ($[7] !== t3) {
    t4 = {
      enabled: t3,
      suspense: false,
      retry: _temp,
      onError: _temp2
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: tests,
    isLoading,
    error: error_1
  } = useQuery(testsQueryKey, t2, t4);
  let t5;
  bb0: {
    if (!tests) {
      let t6;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t5 = t6;
      break bb0;
    }
    let jobIds;
    if ($[10] !== tests) {
      jobIds = new Set();
      tests.forEach(test => {
        if (test.test_status === "fail" || test.test_status === "error") {
          jobIds.add(test.job_id);
        }
      });
      $[10] = tests;
      $[11] = jobIds;
    } else {
      jobIds = $[11];
    }
    let t6;
    if ($[12] !== jobIds) {
      t6 = Array.from(jobIds);
      $[12] = jobIds;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = t6;
  }
  const failingJobIds = t5;
  let t6;
  if ($[14] !== tests) {
    t6 = tests || [];
    $[14] = tests;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = error_1?.response?.status === 404 ? null : error_1;
  let t8;
  if ($[16] !== failingJobIds || $[17] !== isLoading || $[18] !== t6 || $[19] !== t7) {
    t8 = {
      tests: t6,
      isLoading,
      error: t7,
      failingJobIds
    };
    $[16] = failingJobIds;
    $[17] = isLoading;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp2(error_0) {
  if (error_0?.response?.status === 404) {
    return;
  }
}
function _temp(failureCount, error) {
  if (error?.response?.status === 404) {
    return false;
  }
  return failureCount < 3;
}